// store/jobCounts.js
import { defineStore } from 'pinia';
import { useUserStore } from '@store/user';
import { useDashboardStore } from '@store/dashboard';
import { toast } from 'vue-sonner';

export const useJobCountsStore = defineStore('jobCounts', {
    state: () => ({
        newLeadsCount: 0,
        pendingJobsCount: 0,
        conflictsCount: 0,
        previousNewLeadsCount: 0,
        previousPendingJobsCount: 0,
        isFetching: false,
        error: null,
        listenerLead: null,
        listenerJobConfirmed: null,
        listenerLeadsCount: null,
        listenerJobsCount: null,
        listenerFixList: null,
        _fixListDebounceTimer: null,
        isInitialized: false,
    }),
    actions: {
        async fetchNewLeadsCount() {
            const userStore = useUserStore();
            const dashboardStore = useDashboardStore();
            const tenant = userStore.getTenant;
            const companyId = tenant?.default_company_id;
            if (!companyId) {
                console.log('No company ID found for fetching new leads count');
                return;
            }
            try {
                const response = await dashboardStore.retrieveLeadsCount(companyId, 'NEW_LEAD');
                const newCount = response?.count || 0;

                // Check if this is not the initial fetch and count has increased
                if (this.isInitialized && newCount > this.newLeadsCount) {
                    const increase = newCount - this.newLeadsCount;
                    toast.success(`${increase} new lead${increase > 1 ? 's' : ''} available!`);
                }

                this.previousNewLeadsCount = this.newLeadsCount;
                this.newLeadsCount = newCount;
            } catch (err) {
                console.error('Error fetching new leads count:', err);
                this.error = err;
            }
        },

        async fetchPendingJobsCount() {
            const userStore = useUserStore();
            const dashboardStore = useDashboardStore();
            const tenant = userStore.getTenant;
            const companyId = tenant?.default_company_id;
            if (!companyId) {
                console.log('No company ID found for fetching pending jobs count');
                return;
            }
            try {
                const response = await dashboardStore.retrieveJobsCount(companyId, 'PENDING');
                const newCount = response?.count || 0;

                // Check if this is not the initial fetch and count has increased
                if (this.isInitialized && newCount > this.pendingJobsCount) {
                    const increase = newCount - this.pendingJobsCount;
                    toast.success(`${increase} new job confirmation${increase > 1 ? 's' : ''} available!`);
                }

                this.previousPendingJobsCount = this.pendingJobsCount;
                this.pendingJobsCount = newCount;
            } catch (err) {
                console.error('Error fetching pending jobs count:', err);
                this.error = err;
            }
        },

        async fetchConflictsCount() {
            try {
                const response = await window.tenantAxios.get('/diaries/fix-list/count', { xSilent: true });
                this.conflictsCount = response.data?.total || 0;
            } catch (_err) {
                // Silently ignore — user may not have scheduler-view permission
            }
        },

        async fetchCounts() {
            this.isFetching = true;
            this.error = null;
            try {
                await Promise.all([
                    this.fetchNewLeadsCount(),
                    this.fetchPendingJobsCount(),
                    this.fetchConflictsCount(),
                ]);
                // Mark as initialized after first successful fetch
                if (!this.isInitialized) {
                    this.isInitialized = true;
                }
            } catch (err) {
                this.error = err;
            } finally {
                this.isFetching = false;
            }
        },

        startListener() {
            const userStore = useUserStore();
            const tenant = userStore.getTenant;
            const companyId = tenant?.default_company_id;
            if (!window.echo || !companyId) {
                return;
            }

            this.listenerLead = window.echo.private(`new.lead.${companyId}`).listen('NewLeadCreatedEvent', (e) => {
                const ref = e.order_ref || e.orderRef || (e.order && e.order.ref);
                toast.success(`New lead created (Order #${ref})`);
            });

            this.listenerJobConfirmed = window.echo
                .private(`job.confirmed.${companyId}`)
                .listen('JobConfirmedEvent', (e) => {
                    const ref = e.order_ref || e.orderRef || (e.order && e.order.ref);
                    toast.success(`Job confirmed (Order #${ref})`);
                });

            this.listenerLeadsCount = window.echo
                .private(`leads.count.${companyId}`)
                .listen('LeadsCountUpdatedEvent', (e) => {
                    const newCount = e.count || 0;

                    if (this.isInitialized && newCount > this.newLeadsCount) {
                        const increase = newCount - this.newLeadsCount;
                        toast.success(`${increase} new lead${increase > 1 ? 's' : ''} available!`);
                    }

                    this.previousNewLeadsCount = this.newLeadsCount;
                    this.newLeadsCount = newCount;
                });

            this.listenerJobsCount = window.echo
                .private(`jobs.count.${companyId}`)
                .listen('JobsCountUpdatedEvent', (e) => {
                    const newCount = e.count || 0;

                    if (this.isInitialized && newCount > this.pendingJobsCount) {
                        const increase = newCount - this.pendingJobsCount;
                        toast.success(`${increase} new job confirmation${increase > 1 ? 's' : ''} available!`);
                    }

                    this.previousPendingJobsCount = this.pendingJobsCount;
                    this.pendingJobsCount = newCount;
                });

            const tenantId = tenant?.id;
            if (tenantId) {
                this.listenerFixList = window.echo.private(`tenant.${tenantId}`).listen('FixListUpdatedEvent', () => {
                    if (this._fixListDebounceTimer) {
                        clearTimeout(this._fixListDebounceTimer);
                    }
                    this._fixListDebounceTimer = setTimeout(() => {
                        this.fetchConflictsCount();
                    }, 2000);
                });
            }
        },

        stopListener() {
            if (this.listenerLead) {
                this.listenerLead = null;
            }
            if (this.listenerJobConfirmed) {
                this.listenerJobConfirmed = null;
            }
            if (this.listenerLeadsCount) {
                this.listenerLeadsCount = null;
            }
            if (this.listenerJobsCount) {
                this.listenerJobsCount = null;
            }
            if (this.listenerFixList) {
                this.listenerFixList = null;
            }
            if (this._fixListDebounceTimer) {
                clearTimeout(this._fixListDebounceTimer);
                this._fixListDebounceTimer = null;
            }
        },

        resetCounts() {
            this.newLeadsCount = 0;
            this.pendingJobsCount = 0;
            this.conflictsCount = 0;
            this.previousNewLeadsCount = 0;
            this.previousPendingJobsCount = 0;
            this.isInitialized = false;
        },
    },
    getters: {
        getNewLeadsCount: (state) => state.newLeadsCount,
        getPendingJobsCount: (state) => state.pendingJobsCount,
        getConflictsCount: (state) => state.conflictsCount,
        getPreviousNewLeadsCount: (state) => state.previousNewLeadsCount,
        getPreviousPendingJobsCount: (state) => state.previousPendingJobsCount,
        getIsFetching: (state) => state.isFetching,
        getError: (state) => state.error,
        getIsInitialized: (state) => state.isInitialized,
        getNewLeadsIncrease: (state) =>
            state.isInitialized ? Math.max(0, state.newLeadsCount - state.previousNewLeadsCount) : 0,
        getPendingJobsIncrease: (state) =>
            state.isInitialized ? Math.max(0, state.pendingJobsCount - state.previousPendingJobsCount) : 0,
    },
});
